
import { defineComponent } from 'vue'

// paper information
interface PaperInfo {
  title: string,
  author: string,
  arxiv: string,
  journal?: string,
  volume?: string,
  page?: string,
  year?: number,
  url?: string,
  note?: string,
  comment?: string,
  bibtex: string
}

export default defineComponent({
  data () {
    return {
      papers: [
        {
          title: 'Boundary condition for phonon distribution functions at a smooth crystal interface and interfacial angular momentum transfer',
          author: 'Yuta Suzuki, <u>Shuntaro Sumita</u>, and Yusuke Kato',
          arxiv: '2601.18584',
          bibtex:
          `@misc{Suzuki2026_arXiv,
  title={{Boundary condition for phonon distribution functions at a smooth crystal interface and interfacial angular momentum transfer}}, 
  author={Yuta Suzuki and Shuntaro Sumita and Yusuke Kato},
  year={2026},
  eprint={2601.18584},
  archivePrefix={arXiv}
}`
        },
        {
          title: 'Chirality-dependent spin polarization in metals: linear and quadratic responses',
          author: 'Kosuke Yoshimi, Yusuke Kato, Yuta Suzuki, <u>Shuntaro Sumita</u>, Takuro Sato, Hiroshi M. Yamamoto, Yoshihiko Togawa, Hiroaki Kusunose, and Jun-ichiro Kishine',
          arxiv: '2408.04450',
          journal: 'Phys. Rev. B',
          volume: '113',
          page: '155112',
          year: 2026,
          url: 'https://journals.aps.org/prb/abstract/10.1103/95kq-fss8',
          bibtex:
          `@article{Yoshimi2026,
  title = {{Chirality-dependent spin polarization in metals: linear and quadratic responses}},
  author = {Yoshimi, Kosuke and Kato, Yusuke and Suzuki, Yuta and Sumita, Shuntaro and Sato, Takuro and Yamamoto, Hiroshi M. and Togawa, Yoshihiko and Kusunose, Hiroaki and Kishine, Jun-ichiro},
  journal = {Phys. Rev. B},
  volume = {113},
  issue = {15},
  pages = {155112},
  numpages = {14},
  year = {2026},
  month = {Apr},
  publisher = {American Physical Society},
  doi = {10.1103/95kq-fss8},
  url = {https://link.aps.org/doi/10.1103/95kq-fss8}
}`
        },
        {
          title: 'Phase-modulated superconductivity via altermagnetism',
          author: '<u>Shuntaro Sumita</u>, Makoto Naka, and Hitoshi Seo',
          arxiv: '2506.22297',
          journal: 'Phys. Rev. B',
          volume: '112',
          page: '144510',
          year: 2025,
          url: 'https://journals.aps.org/prb/abstract/10.1103/3k12-2467',
          comment: '<a href="https://arxiv.org/abs/2308.14227" target="_blank">2308.14227</a>の研究の続編．交替磁性と超伝導が共存すると，クーパー対が有限の重心運動量をもつFulde–Ferrell–Larkin–Ovchinnikov (FFLO)状態が安定化しうることは我々を含め既に多くのグループで示されているが，今回はこのFFLO超伝導の空間構造を調べる研究を行った．微視的な模型に基づいて構築したGinzburg–Landau (GL)理論の解析から，交替磁性秩序のもとでは秩序変数の振幅が変調するLO状態よりも，位相が変調するFF状態の方が安定化しうることを明らかにした．Zeeman磁場下の超伝導の場合は通常LO状態の方が安定になるので，これはやや意外な結果である．さらに他の模型も解析して比較していくと，この位相変調FF超伝導の発現には副格子自由度が重要であることも分かった．交替磁性の理論研究では，(解析を簡単にするため)副格子を陽に考慮しない代わりにZeeman場に異方的な波数依存性を持たせるということが多く行われているが，この取り扱いでは正しく現象を記述できない場合があることを本研究は示している．正直最初にGLの計算結果を見たときは「何だこれ？」となったのだが，色々チェックを重ねて恐らく間違いではないだろうという結論に至った．研究の着想段階では違うことを考えていたのだが，計算を進めていく中で知らない現象に出会うという，ある意味では自分が理想とする研究の形の1つだったかもしれない．(2025.6.30)',
          bibtex:
          `@article{Sumita2025,
  title = {{Phase-modulated superconductivity via altermagnetism}},
  author = {Sumita, Shuntaro and Naka, Makoto and Seo, Hitoshi},
  journal = {Phys. Rev. B},
  volume = {112},
  issue = {14},
  pages = {144510},
  numpages = {18},
  year = {2025},
  month = {Oct},
  publisher = {American Physical Society},
  doi = {10.1103/3k12-2467},
  url = {https://link.aps.org/doi/10.1103/3k12-2467}
}`
        },
        {
          title: 'Theory of phonon angular momentum transport across a smooth crystal interface',
          author: 'Yuta Suzuki, <u>Shuntaro Sumita</u>, and Yusuke Kato',
          arxiv: '2409.08874',
          bibtex:
          `@misc{Suzuki2024_arXiv,
  title={{Theory of phonon angular momentum transport across a smooth crystal interface}}, 
  author={Yuta Suzuki and Shuntaro Sumita and Yusuke Kato},
  year={2024},
  eprint={2409.08874},
  archivePrefix={arXiv}
}`
        },
        {
          title: 'Anisotropy-induced spin parity effects',
          author: '<u>Shuntaro Sumita</u>, Akihiro Tanaka, and Yusuke Kato',
          arxiv: '2402.19311',
          journal: 'Phys. Rev. B',
          volume: '110',
          page: 'L100403',
          year: 2024,
          url: 'https://journals.aps.org/prb/abstract/10.1103/PhysRevB.110.L100403',
          comment: '東大へ移ってから始めた研究．スピンの整数・半奇整数の違いによって物理的なふるまいが変化する「スピンパリティ効果」が，大きな容易面異方性によって一般に起こることを示した．着任する直前，2022年8月にあった北海道での研究会(LT29)で，カイラル強磁性鎖のスピンパリティ効果に関する<a href="https://journals.aps.org/prb/abstract/10.1103/PhysRevB.107.024403" target="_blank">児玉さんの研究</a>について加藤さんのポスター発表を聞き，量子スピン系に興味を持ったのがきっかけ．そろそろ超伝導以外のテーマをやってみたいという気持ちもあったので，まずは交換相互作用を反強磁性的にしたらどうなるだろうか？という安直な発想からスタートして数値計算をいろいろ行った．その後紆余曲折あったが，2023年6月にNIMSで集まって数値計算結果を議論している最中に，異方性が重要であることを田中さんが見抜き，今の形にまとまった．もう一つの個人的ハイライトは，Perron–Frobeniusの定理を用いて基底状態がもつZ<sub>2</sub>対称性(z軸まわりのスピンのπ回転)の固有値と結晶運動量の関係を示せたこと(これの証明のせいでサプリが長くなったが)．自分にとっては初めての分野で勘が働かないところもあり大変だったが，共同研究者の助けもあって多くのことを学ぶことができた．(2024.3.1)',
          bibtex:
          `@article{PhysRevB.110.L100403,
  title = {{Anisotropy-induced spin parity effects}},
  author = {Sumita, Shuntaro and Tanaka, Akihiro and Kato, Yusuke},
  journal = {Phys. Rev. B},
  volume = {110},
  issue = {10},
  pages = {L100403},
  numpages = {6},
  year = {2024},
  month = {Sep},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevB.110.L100403},
  url = {https://link.aps.org/doi/10.1103/PhysRevB.110.L100403}
}`
        },
        {
          title: 'Ideal spin-orbit-free Dirac semimetal and diverse topological transitions in Y<sub>8</sub>CoIn<sub>3</sub> family',
          author: 'Manabu Sato, Juba Bouaziz, <u>Shuntaro Sumita</u>, Shingo Kobayashi, Ikuma Tateishi, Stefan Blügel, Akira Furusaki, and Motoaki Hirayama',
          arxiv: '2401.13930',
          journal: 'Commun. Mater.',
          volume: '5',
          page: '253',
          year: 2024,
          url: 'https://doi.org/10.1038/s43246-024-00635-9',
          comment: '物質群<i>RE</i><sub>8</sub>Co<i>X</i> (<i>RE</i> = レアアース; <i>X</i> = Al, Ga, In)は，スピン軌道相互作用がなくスピン自由度をもたない状況においてもΓ-A線上にDirac点が現れることを第一原理計算に基づいて示した研究．このDirac点は，結晶点群C<sub>6v</sub>の相異なる2次元表現に属する2本のバンドの交差によって生じている．特にY<sub>8</sub>CoIn<sub>3</sub>をはじめとするいくつかの物質では，フェルミ準位付近にDirac点以外の余計なバンドが現れず，理想的なスピンレスDirac半金属であると言える．第一著者の佐藤さんがかなり頑張って計算を進めてくれた．非磁性の第一原理計算以外にも磁性を入れた場合の計算，またk・p模型や有効強束縛模型の構築と解析なども行っており，著者それぞれの持ち味も出ているのではと思う．(2024.1.26)',
          bibtex:
          `@article{CommunMater.5.253,
  title={{Ideal spin-orbit-free Dirac semimetal and diverse topological transitions in Y$_8$CoIn$_3$ family}},
  author={Sato, Manabu and Bouaziz, Juba and Sumita, Shuntaro and Kobayashi, Shingo and Tateishi, Ikuma and Bl\\"ugel, Stefan and Furusaki, Akira and Hirayama, Motoaki},
  journal={Commun. Mater.},
  volume={5},
  pages={253},
  year={2024},
  doi={10.1038/s43246-024-00635-9},
  url={https://doi.org/10.1038/s43246-024-00635-9}
}`
        },
        {
          title: 'Spin-orbit enabled unconventional Stoner magnetism',
          author: 'Yue Yu, Tatsuya Shishidou, <u>Shuntaro Sumita</u>, Michael Weinert, and Daniel F. Agterberg',
          arxiv: '2310.00838',
          journal: 'Proc. Natl. Acad. Sci.',
          volume: '121',
          page: 'e2411038121',
          year: 2024,
          url: 'https://www.pnas.org/doi/10.1073/pnas.2411038121',
          comment: '2023年の7〜8月に1ヶ月ほどミルウォーキーに滞在していた際に行った共同研究．ある特定の非共型空間群ではブリルアンゾーン境界で通常の電子スピンとは異なる変換性をもつ擬スピン状態が現れるが，Stoner不安定性によってこの擬スピンの強磁性が起こると，元のスピンに関しては正味の磁化をもたない非共線型の磁気秩序が実現する，というもの．future workに繋がりそうな示唆的な結果も多いのではないかと思う．私自身はこの磁気状態で実現する縮退線の位置を対称性に基づいて同定し，またその縮退線に起因するトポロジカルな性質を調べたりした．(2023.10.3)',
          bibtex:
          `@article{PNAS.121.e2411038121,
  author = {Yu, Yue and Shishidou, Tatsuya and Sumita, Shuntaro and Weinert, Michael and Agterberg, Daniel F.},
  title = {{Spin–orbit enabled unconventional Stoner magnetism}},
  journal = {Proc. Natl. Acad. Sci.},
  volume = {121},
  number = {46},
  pages = {e2411038121},
  year = {2024},
  doi = {10.1073/pnas.2411038121},
  URL = {https://www.pnas.org/doi/abs/10.1073/pnas.2411038121}
}`
        },
        {
          title: 'Fulde–Ferrell–Larkin–Ovchinnikov state induced by antiferromagnetic order in κ-type organic conductors',
          author: '<u>Shuntaro Sumita</u>, Makoto Naka, and Hitoshi Seo',
          arxiv: '2308.14227',
          journal: 'Phys. Rev. Research',
          volume: '5',
          page: '043171',
          year: 2023,
          url: 'https://journals.aps.org/prresearch/abstract/10.1103/PhysRevResearch.5.043171',
          note: '<a href="https://www.c.u-tokyo.ac.jp/info/news/topics/20231128140000.html" target="_blank">プレスリリース</a>',
          comment: '擬2次元的な有機化合物群κ-(BEDT-TTF)<sub>2</sub><i>X</i> (<i>X</i>: 1価の陰イオン)において，反強磁性と超伝導が共存するとクーパー対が有限の重心運動量をもつFulde–Ferrell–Larkin–Ovchinnikov (FFLO)状態が安定化しうることを示した論文．この物質群で反強磁性を示すもの(κ-(BEDT-TTF)<sub>2</sub>Cu[N(CN)<sub>2</sub>]Clなど)は，通常のコリニア型の反強磁性であるにも関わらず，その特徴的な分子配列のために磁気状態ではエネルギーバンド構造に異方的なスピン分裂が生じることが共同研究者の中さんや妹尾さんらによって指摘されている．理研にポスドクとして居た頃にお二人からこの話を教えて頂き，「時間反転対称性が破れているのだからFFLOを起こせるのでは？」と思い研究を始めた．論文ではバンド内の引力相互作用を仮定した解析と，FLEX近似に基づくHubbard模型の多体計算の2つのアプローチを行っている．FLEXのコードを書くのは初めてだったので後者の計算にものすごく時間がかかり，着想から論文を出すまで1年以上かかってしまった．なお，本研究の肝となる異方的スピン分裂を伴う磁性は最近では「altermagnetism」という用語で盛んに研究されており，(その意図は全くなかったが)奇しくも流行に乗っかる形になってしまった．(2023.8.29)',
          bibtex:
          `@article{PhysRevResearch.5.043171,
  title = {{Fulde-Ferrell-Larkin-Ovchinnikov state induced by antiferromagnetic order in $\\ensuremath{\\kappa}$-type organic conductors}},
  author = {Sumita, Shuntaro and Naka, Makoto and Seo, Hitoshi},
  journal = {Phys. Rev. Res.},
  volume = {5},
  issue = {4},
  pages = {043171},
  numpages = {17},
  year = {2023},
  month = {Nov},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevResearch.5.043171},
  url = {https://link.aps.org/doi/10.1103/PhysRevResearch.5.043171}
}`
        },
        {
          title: 'Crystal symmetry protected gapless vortex line phases in superconducting Dirac semimetals',
          author: 'Shingo Kobayashi, <u>Shuntaro Sumita</u>, Motoaki Hirayama, and Akira Furusaki',
          arxiv: '2302.07451',
          journal: 'Phys. Rev. B',
          volume: '107',
          page: '214518',
          year: 2023,
          url: 'https://journals.aps.org/prb/abstract/10.1103/PhysRevB.107.214518',
          comment: '理研時代に行った共同研究．フェルミ準位付近にDirac点(と金属的なバンド)をもつような半金属において，超伝導状態で磁場による渦糸が入った際に発現しうるギャップレス渦線相の研究を行った．特に，回転ないし螺旋対称性によって守られるギャップレス渦線相について，低エネルギー有効ハミルトニアンを用いて詳細な解析・分類を与えた．後半では具体的な強束縛模型の計算や候補物質の議論も行っている．またmain partではないが，Appendix C (2023.7.1追記：出版版ではAppendix D)の説明は光が丘公園をぼーっと散歩しているときに思い付いたもので，個人的には印象に残っている．(2023.2.16)',
          bibtex:
          `@article{PhysRevB.107.214518,
  title = {{Crystal symmetry protected gapless vortex line phases in superconducting Dirac semimetals}},
  author = {Kobayashi, Shingo and Sumita, Shuntaro and Hirayama, Motoaki and Furusaki, Akira},
  journal = {Phys. Rev. B},
  volume = {107},
  issue = {21},
  pages = {214518},
  numpages = {18},
  year = {2023},
  month = {Jun},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevB.107.214518},
  url = {https://link.aps.org/doi/10.1103/PhysRevB.107.214518}
}`
        },
        {
          title: 'Supercurrent-Induced Weyl Superconductivity',
          author: '<u>Shuntaro Sumita</u> and Kazuaki Takasan',
          arxiv: '2201.07397',
          journal: 'J. Phys. Soc. Jpn.',
          volume: '91',
          page: '074703',
          year: 2022,
          url: 'https://journals.jps.jp/doi/abs/10.7566/JPSJ.91.074703',
          comment: '<a href="https://arxiv.org/abs/2110.06959" target="_blank">2110.06959</a>の研究をしている際，「2次元D+p波超伝導体に電流を流すとき，面内のRashba項以外に面直のスピン軌道相互作用を入れないとギャップを開けられない」という結果を高三さんから聞き，「3次元系ならRashbaと同じ対称性で自然に面直成分が入るのではないか？」と思いスタートした研究．結果は予想通りで，元々ある線ノードが超伝導電流によって一部ギャップアウトされてWeyl点になることが分かった．Weylチャージが電流の方向によって変わっていくというのも面白い結果だと思う．余談だが，上記の論文中で議論している量子化Berry位相によるノードの保護は，この3次元系の計算を並行して行っていたために気付けたという経緯がある．(2022.1.20)',
          note: `<b><span class="text-success">Editors' Choice</span></b><br/>
          <a href="https://doi.org/10.7566/JPSHT.2.028" target="_blank">JPS Hot Topics</a>，<a href="https://doi.org/10.7566/JPSJNC.19.15" target="_blank">JPSJ News Comments</a>で紹介されました．`,
          bibtex:
          `@article{JPSJ.91.074703,
  author = {Sumita, Shuntaro and Takasan, Kazuaki},
  title = {{Supercurrent-Induced Weyl Superconductivity}},
  journal = {J. Phys. Soc. Jpn.},
  volume = {91},
  number = {7},
  pages = {074703},
  year = {2022},
  doi = {10.7566/JPSJ.91.074703},
  URL = {https://journals.jps.jp/doi/abs/10.7566/JPSJ.91.074703}
}`
        },
        {
          title: 'Supercurrent-induced topological phase transitions',
          author: 'Kazuaki Takasan, <u>Shuntaro Sumita</u>, and Youichi Yanase',
          arxiv: '2110.06959',
          journal: 'Phys. Rev. B',
          volume: '106',
          page: '014508',
          year: 2022,
          url: 'https://journals.aps.org/prb/abstract/10.1103/PhysRevB.106.014508',
          comment: '超伝導体に電流を流すとBogoliubovスペクトルが非対称に変形するが，それを利用することでトポロジカル相転移を起こせるのではないか？という提案．根本となるアイデアは高三さんが着想されたものだが，数値計算で現れたエッジ状態のトポロジカルな特徴付けについて議論して頂くうちに共同研究に発展した．「こういうトポロジカル数があるんじゃないか？」と思い付いて実際にそれが非自明になったときは少し嬉しかった．高三さんは大学院時代の先輩で長くお世話になっているが，共同研究をするのは初めてだったので感慨深く思っている．(2021.10.15)',
          bibtex:
          `@article{PhysRevB.106.014508,
  title = {{Supercurrent-induced topological phase transitions}},
  author = {Takasan, Kazuaki and Sumita, Shuntaro and Yanase, Youichi},
  journal = {Phys. Rev. B},
  volume = {106},
  issue = {1},
  pages = {014508},
  numpages = {14},
  year = {2022},
  month = {Jul},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevB.106.014508},
  url = {https://link.aps.org/doi/10.1103/PhysRevB.106.014508}
}`
        },
        {
          title: 'Dirac lines and loop at the Fermi level in the time-reversal symmetry breaking superconductor LaNiGa<sub>2</sub>',
          author: 'Jackson R. Badger, Yundi Quan, Matthew C. Staab, <u>Shuntaro Sumita</u>, Antonio Rossi, Kasey P. Devlin, Kelly Neubauer, Daniel S. Shulman, James C. Fettinger, Peter Klavins, Susan M. Kauzlarich, Dai Aoki, Inna M. Vishik, Warren E. Pickett, and Valentin Taufour',
          arxiv: '2109.06983',
          journal: 'Commun. Phys.',
          volume: '5',
          page: '22',
          year: 2022,
          url: 'https://www.nature.com/articles/s42005-021-00771-5',
          comment: 'アメリカの実験グループとの共同研究．超伝導体LaNiGa<sub>2</sub>の結晶構造は共型な空間群Cmmmに属すると考えられていたが，単結晶X線回折の測定から実は非共型なCmcmという空間群であるということを明らかにした，というのがメインの結果．非共型であることから超伝導ギャップ構造も共型なものとは異なることが予想されるので，その分類を実際に行ったというのが私の主なcontributionである．2020年末にTaufour教授から「君の博論に興味があるので教えてほしい」と急にメールが来たので驚いたが，結果的にこうして論文にできたので良かったと思う．実験家ないし海外の研究者と共同研究をするのは初めてなので，その意味でも少し嬉しい．(2021.9.16)',
          bibtex:
          `@article{CommunPhys.5.22,
  title={{Dirac lines and loop at the Fermi level in the time-reversal symmetry breaking superconductor LaNiGa$_2$}},
  author={Badger, Jackson R. and Quan, Yundi and Staab, Matthew C. and Sumita, Shuntaro and Rossi, Antonio and Devlin, Kasey P. and Neubauer, Kelly and Shulman, Daniel S. and Fettinger, James C. and Klavins, Peter and Kauzlarich, Susan M. and Aoki, Dai and Vishik, Inna M. and Pickett, Warren E. and Taufour, Valentin},
  journal={Commun. Phys.},
  volume={5},
  pages={22},
  number={1},
  year={2022},
  month={Jan},
  day={13},
  issn={2399-3650},
  doi={10.1038/s42005-021-00771-5},
  url={https://doi.org/10.1038/s42005-021-00771-5}
}`
        },
        {
          title: 'Superconductor/normal-metal/superconductor junction of topological superconductors revisited: Fractional Josephson current, fermion parity, and oscillating wave functions',
          author: '<u>Shuntaro Sumita</u> and Akira Furusaki',
          arxiv: '2109.02918',
          journal: 'Phys. Rev. B',
          volume: '104',
          page: '205431',
          year: 2021,
          url: 'https://journals.aps.org/prb/abstract/10.1103/PhysRevB.104.205431',
          comment: '理研に着任してから行った初めての研究．有限系のKitaev鎖では両端のマヨラナゼロモードが混成して小さいギャップが開くが，その開き方が系のパラメータによって振動し基底状態のフェルミオンパリティがスイッチするという<a href="https://journals.aps.org/prb/abstract/10.1103/PhysRevB.94.115166" target="_blank">論文</a>を読み，SNS接合系ではどうなるのだろうか？という疑問からスタートして摂動論とか厳密解とか計算した．あまり派手な結果ではないが，これまでと若干毛色の違う研究で色々勉強になった．着任直後で慣れない環境の中，コロナ禍で在宅勤務が基本になって結構苦労したが，なんとか形にすることができて少し安心している．(2021.9.8)',
          bibtex:
          `@article{PhysRevB.104.205431,
  title = {{Superconductor/normal-metal/superconductor junction of topological superconductors revisited: Fractional Josephson current, fermion parity, and oscillating wave functions}},
  author = {Sumita, Shuntaro and Furusaki, Akira},
  journal = {Phys. Rev. B},
  volume = {104},
  issue = {20},
  pages = {205431},
  numpages = {14},
  year = {2021},
  month = {Nov},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevB.104.205431},
  url = {https://link.aps.org/doi/10.1103/PhysRevB.104.205431}
}`
        },
        {
          title: 'Superconductivity induced by fluctuations of momentum-based multipoles',
          author: '<u>Shuntaro Sumita</u> and Youichi Yanase',
          arxiv: '2004.08086',
          journal: 'Phys. Rev. Research',
          volume: '2',
          page: '033225',
          year: 2020,
          url: 'https://journals.aps.org/prresearch/abstract/10.1103/PhysRevResearch.2.033225',
          comment: '<a href="https://arxiv.org/abs/1603.09440" target="_blank">1603.09440</a>や<a href="https://arxiv.org/abs/1702.08659" target="_blank">1702.08659</a>では磁気多極子秩序によって対称性が破れた相の超伝導状態に注目していたが，今回はむしろ"破れる寸前"の相に注目し，臨界点近くの多極子揺らぎによってどんな超伝導状態が現れるかを研究した．2015年の<a href="https://journals.aps.org/prl/abstract/10.1103/PhysRevLett.115.207002" target="_blank">Kozii-Fuの研究</a>では，スピン軌道相互作用がある系において「奇パリティの電気的揺らぎによってスピン三重項超伝導が誘起される」という結果が得られていたが，磁気的揺らぎの場合や偶パリティの場合にはどうなるのかということが気になって調べ始めたのがきっかけ．Kozii-Fuではあまり議論されていなかった結晶場の効果なども考えている(特に，上記のスピン三重項超伝導は等方的な場合にはTcがかなり低いのに対し，結晶場を入れると高くなるケースがあることは重要な結果)．最初はコンパクトな論文にしようと思っていたのだが，分類表や候補物質を盛り込んでいるうちに妙に長くなってしまった．',
          bibtex:
          `@article{PhysRevResearch.2.033225,
  title = {{Superconductivity induced by fluctuations of momentum-based multipoles}},
  author = {Sumita, Shuntaro and Yanase, Youichi},
  journal = {Phys. Rev. Research},
  volume = {2},
  issue = {3},
  pages = {033225},
  numpages = {19},
  year = {2020},
  month = {Aug},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevResearch.2.033225},
  url = {https://link.aps.org/doi/10.1103/PhysRevResearch.2.033225}
}`
        },
        {
          title: 'Insulator-Metal Transition and Topological Superconductivity in UTe<sub>2</sub> from a First-Principles Calculation',
          author: 'Jun Ishizuka, <u>Shuntaro Sumita</u>, Akito Daido, and Youichi Yanase',
          arxiv: '1908.04004',
          journal: 'Phys. Rev. Lett.',
          volume: '123',
          page: '217001',
          year: 2019,
          url: 'https://journals.aps.org/prl/abstract/10.1103/PhysRevLett.123.217001',
          bibtex:
          `@article{PhysRevLett.123.217001,
  title = {{Insulator-Metal Transition and Topological Superconductivity in \${\\mathrm{UTe}}_{2}$ from a First-Principles Calculation}},
  author = {Ishizuka, Jun and Sumita, Shuntaro and Daido, Akito and Yanase, Youichi},
  journal = {Phys. Rev. Lett.},
  volume = {123},
  issue = {21},
  pages = {217001},
  numpages = {6},
  year = {2019},
  month = {Nov},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevLett.123.217001},
  url = {https://link.aps.org/doi/10.1103/PhysRevLett.123.217001}
}`
        },
        {
          title: 'Classification of topological crystalline superconducting nodes on high-symmetry lines: Point nodes, line nodes, and Bogoliubov Fermi surfaces',
          author: '<u>Shuntaro Sumita</u>, Takuya Nomoto, Ken Shiozaki, and Youichi Yanase',
          arxiv: '1811.08627',
          journal: 'Phys. Rev. B',
          volume: '99',
          page: '134513',
          year: 2019,
          url: 'https://journals.aps.org/prb/abstract/10.1103/PhysRevB.99.134513',
          comment: '<a href="https://journals.aps.org/prl/abstract/10.1103/PhysRevLett.118.207001" target="_blank">Micklitz-Norman (2017)</a>や<a href="https://arxiv.org/abs/1801.03293" target="_blank">1801.03293</a>ではブリルアンゾーンの高対称面上の超伝導ギャップ構造を群論的に分類し，線ノードが現れる条件を特定した．さらに，小林氏との共同研究(1711.06421)により，この線ノードはすべて0次元あるいは1次元のトポロジカル数によって特徴付けられることがClifford代数の知識を用いて明らかになった．一方で，ブリルアンゾーンの高対称軸上のギャップ構造については，群論的な分類は明らかとなっているが，それによって分かるノードがトポロジカル数によって特徴付けられているかは自明ではない．そこで，このような軸上の既約表現がもつemergentな対称性(emergent AZ class)に注目し，トポロジカル数の存在を明らかにした．結果として，群論的分類によって特定されるノードはすべてトポロジカル数によっても特徴付けられることが分かった．さらに論文では，系全体の対称性にも注目し，そのようなノードは点ノードなのか，あるいは線ノードや面ノードの一部なのかということも明らかにした．結構職人的な仕事になってしまったが個人的にはお気に入りの論文．',
          bibtex:
          `@article{PhysRevB.99.134513,
  title = {{Classification of topological crystalline superconducting nodes on high-symmetry lines: Point nodes, line nodes, and Bogoliubov Fermi surfaces}},
  author = {Sumita, Shuntaro and Nomoto, Takuya and Shiozaki, Ken and Yanase, Youichi},
  journal = {Phys. Rev. B},
  volume = {99},
  issue = {13},
  pages = {134513},
  numpages = {19},
  year = {2019},
  month = {Apr},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevB.99.134513},
  url = {https://link.aps.org/doi/10.1103/PhysRevB.99.134513}
}`
        },
        {
          title: 'Unconventional superconducting gap structure protected by space group symmetry',
          author: '<u>Shuntaro Sumita</u> and Youichi Yanase',
          arxiv: '1801.03293',
          journal: 'Phys. Rev. B',
          volume: '97',
          page: '134512',
          year: 2018,
          url: 'https://journals.aps.org/prb/abstract/10.1103/PhysRevB.97.134512',
          comment: '「超伝導対称性の分類」は超伝導の研究分野において重要な課題の一つであるが，その手法の一つとして，<a href="https://journals.aps.org/rmp/abstract/10.1103/RevModPhys.63.239" target="_blank">Sigrist-Uedaのレビュー(1991)</a>で有名な点群の既約表現による超伝導秩序変数の分類が挙げられる．しかし近年，この分類法によって導かれる超伝導ギャップ構造(= Bogoliubov準粒子スペクトルの励起エネルギー)は正しい結果を与えない場合があることが分かってきており，その代わりにブリルアンゾーンの各k点で許されるクーパー対波動関数の対称性を群論を用いて計算する厳密な超伝導ギャップの分類が提案されている．論文では，この厳密な手法を用いることで，ブリルアンゾーン内の高対称面(鏡映面または映進面)上に現れる線ノードの包括的な分類を行った．系に非共型対称性がある場合には，ブリルアンゾーン境界で(Sigrist-Uedaの分類では現れない)非自明な線ノードが現れうることがいくつかの例で知られていたが，今回の分類によってそのような非自明な現象が起こる条件を求めることができた．実はそのような研究は<a href="https://journals.aps.org/prl/abstract/10.1103/PhysRevLett.118.207001" target="_blank">Micklitz-Norman (2017)</a>によって既になされていたが，我々の論文ではさらに空間反転をもちかつprimitiveな59個の空間群に対して上記の条件を当てはめ，分類表を作成した．また一方，ブリルアンゾーン内の高対称軸(n回回転軸)上のノードの分類については先行研究が少なかったため，共型な場合に限って分類を行った．結果として，3回あるいは6回回転対称性を持つ軸上では正常Bloch状態の全角運動量jzに依存したポイントノードの構造が現れることを見出した．また，UPt<sub>3</sub>, UBe<sub>13</sub>, SrPtAsといった現実の物質においてもこの非自明なギャップ構造の存在を示した．',
          bibtex:
          `@article{PhysRevB.97.134512,
  title = {{Unconventional superconducting gap structure protected by space group symmetry}},
  author = {Sumita, Shuntaro and Yanase, Youichi},
  journal = {Phys. Rev. B},
  volume = {97},
  issue = {13},
  pages = {134512},
  numpages = {16},
  year = {2018},
  month = {Apr},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevB.97.134512},
  url = {https://link.aps.org/doi/10.1103/PhysRevB.97.134512}
}`
        },
        {
          title: 'Symmetry-protected line nodes and Majorana flat bands in nodal crystalline superconductors',
          author: 'Shingo Kobayashi, <u>Shuntaro Sumita</u>, Youichi Yanase, and Masatoshi Sato',
          arxiv: '1711.06421',
          journal: 'Phys. Rev. B',
          volume: '97',
          page: '180504(R)',
          year: 2018,
          url: 'https://journals.aps.org/prb/abstract/10.1103/PhysRevB.97.180504',
          bibtex:
          `@article{PhysRevB.97.180504,
  title = {{Symmetry-protected line nodes and Majorana flat bands in nodal crystalline superconductors}},
  author = {Kobayashi, Shingo and Sumita, Shuntaro and Yanase, Youichi and Sato, Masatoshi},
  journal = {Phys. Rev. B},
  volume = {97},
  issue = {18},
  pages = {180504(R)},
  numpages = {5},
  year = {2018},
  month = {May},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevB.97.180504},
  url = {https://link.aps.org/doi/10.1103/PhysRevB.97.180504}
}`
        },
        {
          title: 'Multipole Superconductivity in Nonsymmorphic Sr<sub>2</sub>IrO<sub>4</sub>',
          author: '<u>Shuntaro Sumita</u>, Takuya Nomoto, and Youichi Yanase',
          arxiv: '1702.08659',
          journal: 'Phys. Rev. Lett.',
          volume: '119',
          page: '027001',
          year: 2017,
          url: 'https://journals.aps.org/prl/abstract/10.1103/PhysRevLett.119.027001',
          comment: 'Sr<sub>2</sub>IrO<sub>4</sub>は銅酸化物高温超伝導体とよく似た性質を持つ層状ペロブスカイト構造の絶縁体であり，電子ドープなどで超伝導が発現することが期待されている．また，面内で傾いた反強磁性構造を持つことが知られており，そのスタッキングの仕方が違う−++−, ++++, −+−+という3つ磁気構造が報告されている．このような背景から，磁気構造の中でも−++−と−+−+という2つに注目し，その秩序と共存する超伝導の性質を群論によるギャップ関数の解析および有効モデルを用いた数値計算といった2つの手法を組み合わせて調査した．その結果，−++−状態では磁気的な非共型対称性に守られた非自明な超伝導ギャップ構造が現れ，−+−+状態では磁場を必要としないFFLO超伝導が発現することを明らかにした．',
          bibtex:
          `@article{PhysRevLett.119.027001,
  title = {{Multipole Superconductivity in Nonsymmorphic \${\\mathrm{Sr}}_{2}{\\mathrm{IrO}}_{4}$}},
  author = {Sumita, Shuntaro and Nomoto, Takuya and Yanase, Youichi},
  journal = {Phys. Rev. Lett.},
  volume = {119},
  issue = {2},
  pages = {027001},
  numpages = {6},
  year = {2017},
  month = {Jul},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevLett.119.027001},
  url = {https://link.aps.org/doi/10.1103/PhysRevLett.119.027001}
}`
        },
        {
          title: 'Superconductivity in magnetic multipole states',
          author: '<u>Shuntaro Sumita</u> and Youichi Yanase',
          arxiv: '1603.09440',
          journal: 'Phys. Rev. B',
          volume: '93',
          page: '224507',
          year: 2016,
          url: 'https://journals.aps.org/prb/abstract/10.1103/PhysRevB.93.224507',
          comment: '磁場中の超伝導状態はこれまでに多く議論されてきたテーマである．一方で，固体内での多極子自由度といったものが近年の凝縮系物理学で注目を集めている．そこで，「磁気多極子と共存する超伝導状態」の新奇な性質を調査することを目的とし，1次元ジグザグ格子という簡単なモデルを用いて平均場理論で解析を行った．その結果，磁気単極子状態では通常のBCS超伝導であるが，磁気双極子状態と磁気四極子状態ではそれぞれPDW超伝導・FFLO超伝導が発現するという非自明な性質を見出すことができた．',
          bibtex:
          `@article{PhysRevB.93.224507,
  title = {{Superconductivity in magnetic multipole states}},
  author = {Sumita, Shuntaro and Yanase, Youichi},
  journal = {Phys. Rev. B},
  volume = {93},
  issue = {22},
  pages = {224507},
  numpages = {12},
  year = {2016},
  month = {Jun},
  publisher = {American Physical Society},
  doi = {10.1103/PhysRevB.93.224507},
  url = {https://link.aps.org/doi/10.1103/PhysRevB.93.224507}
}`
        }
      ] as PaperInfo[]
    }
  }
})
